import { Form } from "@inertiajs/react"
import { Package, Pencil } from "lucide-react"
import { type RefObject, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useFocusOnRefusal } from "@/hooks/use-focus-on-refusal"
import { REFUSAL_FOCUS_ATTR, describedBy } from "@/lib/refusal-focus"
import { correctionOffered } from "@/lib/shippable-variants"
import { cn } from "@/lib/utils"
import { variantLabel } from "@/lib/variant-label"
import {
  markShippedRedemptionPath,
  shipBrandShipmentsParcelPath,
  transitionsMissionCollabPath,
} from "@/routes"
import type { ShipVariantOption } from "@/types/missions"
import type { ShipmentProduct } from "@/types/shipments"

import { useShipRefusal } from "./ship-refusal"
import { ShipmentProductBlock } from "./shipment-product-block"

/**
 * OKS-32 — Brand-side `Mark shipped` dialog (UX Spec §2.6).
 *
 * Wraps Inertia `<Form>` (Crew rule) — no shadcn Form, no RHF, no zod.
 * Hidden `event=mark_shipped` input + optional `tracking_number` text
 * input. Server-side validation primary (≤80 chars, allow_blank);
 * field-level error renders from `errors.tracking_number` via the
 * Inertia render-prop.
 *
 * # Two modes
 *
 *   - **`mark` mode** (default — row is in `to_ship`): trigger button
 *     primary "Mark shipped" with Package icon. Dialog title "Mark as
 *     shipped"; submit "Mark shipped". Empty input = ship without
 *     tracking (small-package no-tracking is common per PRD §1).
 *
 *   - **`edit` mode** (row is in `shipped`): the correction surface.
 *     Trigger is a small pencil icon button; dialog title becomes
 *     "Correct the shipment"; the tracking inputs prefill with the current
 *     values; submit becomes "Save". Server treats this as an
 *     idempotent-state re-fire that overwrites the three tracking columns
 *     while preserving `shipped_at` / `shipped_by_user_id`.
 *
 *     OKS-996 — it ALSO carries the variant control, and the write path
 *     re-stamps the recorded variant when (and only when) a non-blank
 *     `variant_id` is submitted. The title changed with it: a dialog that
 *     corrects the variant while calling itself a tracking editor is one
 *     surface describing one act two ways.
 *
 * # Counter (FE candy)
 *
 * Inline char counter bottom-right; subtle until > 70 chars, then
 * visible. Server is the floor — even if the counter lies, the model
 * `length: {maximum: 80}` validation rejects.
 *
 * # A11y
 *
 *   - Input is labeled (visible Label "Tracking number") with
 *     `aria-describedby` pointing at the helper + counter.
 *   - First focus on the **primary** action (operator-confirming, low
 *     blast radius — UX Spec §6 #9).
 *   - Inline error region uses `role="alert"` + `aria-live="polite"`.
 */

const MAX_TRACKING_CHARS = 80
const COUNTER_VISIBILITY_THRESHOLD = 70

/**
 * OKS-984 — the MINIMAL row shape this dialog actually reads.
 *
 * ⚠️ Narrowed from `BrandMissionCollabRow` deliberately. The Shipments queue
 * reuses this dialog but its rows come from a different serializer, and the
 * first draft of that page cast with `as never` to satisfy the wider type —
 * which silences the compiler on exactly the mismatch the type exists to
 * catch. Declaring what the component READS lets both callers satisfy it
 * honestly, and `BrandMissionCollabRow` structurally satisfies it for free.
 *
 * `creator_sizes` stays OPTIONAL and is read via an `in` check: it is the
 * OKS-934 gated payload, ABSENT outside the reveal window. Making it required
 * would force callers to invent a value for a key whose absence is the whole
 * point.
 */
/**
 * OKS-985 — which endpoint the dialog submits to. Discriminated so the
 * mission id is only reachable on the branch that has one; a redemption has
 * no mission, and an optional `missionId` would typecheck on both.
 */
export type MarkShippedTarget =
  | { kind: "collab"; missionId: number }
  | { kind: "redemption" }
  /**
   * OKS-991 — the whole parcel: one tracking number written to every item, in
   * one transaction.
   *
   * ⚠️ A third TARGET KIND, not a third mode. `mode` answers "am I recording a
   * shipment or correcting one"; `target` answers "where does this post". A
   * parcel is the second question, and folding it into `mode` would make the
   * two dimensions non-orthogonal — `edit` on a parcel has no meaning
   * (tracking corrections stay per-item, where the row IS the unit).
   *
   * ⚠️ Carries the ITEM LIST, never a group key. The server re-derives what
   * "one parcel" means from the ids it is handed and refuses a mixed
   * destination; a key would let it ship whatever is in Ready at request
   * time, including a row that arrived after the operator packed the box.
   */
  | { kind: "parcel"; items: { source: "collab" | "redemption"; id: number }[] }

export interface MarkShippedDialogRow {
  id: number
  tracking_number: string | null
  tracking_url?: string | null
  delivery_eta?: string | null
  shipped_variant_label?: string | null
  /**
   * OKS-1028 — the recorded variant's id (`collabs.shipped_product_variant_id`),
   * `null` when nothing is recorded or the variant was hard-deleted. Read ONLY
   * by `correctionOffered`; never a `defaultValue` (see the size-rule note).
   */
  shipped_product_variant_id?: number | null
  creator: { handle: string | null; name: string }
  creator_sizes?: { apparel: string | null; shoe: string | null }
}

interface MarkShippedDialogProps {
  row: MarkShippedDialogRow
  /**
   * OKS-985 KD-6 — WHERE this dialog posts.
   *
   * `{kind: "collab", missionId}` -> the shipped collab transitions endpoint.
   * `{kind: "redemption"}`        -> the redemption mark-shipped endpoint.
   *
   * ⚠️ A DESCRIPTOR, not a URL string. The dialog builds the path through
   * js-routes from a discriminated kind, so a caller cannot hand it an
   * arbitrary destination and no hardcoded URL enters the component.
   *
   * ⚠️ Replaces the previous `missionId: number` prop. Required and
   * non-optional for the OKS-933 reason quoted below on `variantOptions`: an
   * optional target would let a caller omit it, the dialog would fall back to
   * some default endpoint, and the failure would be a silently wrong POST
   * rather than a compile error.
   */
  target: MarkShippedTarget
  /**
   * `"mark"` (default) when the row is in `to_ship` (initial ship);
   * `"edit"` when the row is already `shipped` (typo correction).
   */
  mode?: "mark" | "edit"
  /**
   * OKS-934 — variants the brand may record as shipped.
   *
   * ⚠️ OKS-989 — RENDERING IS DECIDED BY LENGTH, never by a variant mode:
   *
   *   `0`   → nothing at all.
   *   `1`   → a readonly statement, and **nothing is submitted** (the server
   *           records the sole member; see `sole_shippable_variant`).
   *   `>=2` → the `<select>`.
   *
   * OKS-996 keeps that size rule identical in `edit` mode, but the select's
   * first option differs in MEANING and therefore in string: `mark` opens on
   * « Non précisé » (do not record), `edit` on « Laisser tel quel » (do not
   * change). At `0` `edit` renders no control; at `1` it renders one unless the
   * row records THAT variant, by id (OKS-1028 D1, `correctionOffered`) — then
   * the `recorded` line already states the fact.
   *
   * BOTH callers pass a PER-ROW set (`product.shippable_variants`) — the queue
   * always has, the mission panel since OKS-995. On a PRE-SHIP row that set is
   * collapsed to the creator's pick when there is one, so a chosen item lands
   * on the `1` branch and the ship-time surface never offers an alternative to
   * what the creator chose. A row with no pick keeps its full set: that is
   * OKS-990 UX-A5's editable field, deliberately left as it is so a brand can
   * record what physically shipped.
   *
   * ⚠️ OKS-996 — ON A `shipped` ROW THE QUEUE'S SET IS **NOT** COLLAPSED, and
   * that asymmetry is the whole feature. `Shipments::ProductPayload` flips it
   * from the record's own state, so the correction dialog offers every shippable
   * variant even for a chosen row — otherwise the ticket's headline case (a
   * brand that packed W against a pick of V) would meet a statement where it
   * needs a control. Do not "restore consistency" by collapsing here: read
   * `ProductPayload.shippable_variants`' header first, which explains why the
   * exception is keyed to the state and must never reach a ship-time payload.
   *
   * Original OKS-934 note, still true for the `0` case: UX §2.1 V3, the select
   * is omitted entirely rather than rendered
   * empty — nothing to choose is not an error state.
   */
  /**
   * ⚠️ REQUIRED, not optional — and that is the OKS-933 lesson applied rather
   * than re-learned. There, an optional `catalog?:` meant the parent never
   * passing it TYPE-CHECKED, the picker rendered its empty branch forever, and
   * the ticket's headline affordance was dead in the UI while the server was
   * correct. Optionality was the defect; the missing prop was a symptom an
   * optional type made unobservable.
   *
   * Required makes the compiler enumerate every hop of
   * page → panel → row-actions → dialog. `[]` is a meaningful VALUE here
   * (UX §2.1 V3 — omit the select), so it must be passed deliberately, never
   * defaulted into existence.
   */
  variantOptions: ShipVariantOption[]
  /**
   * OKS-987 — the parcel this dialog is confirming. Rendered as a strip in the
   * header so the final confirmation states WHAT is being shipped, not only to
   * whom.
   *
   * ⚠️ REQUIRED and NULLABLE, not optional. `null` is a meaningful value (the
   * mission has no gift product), so it must be passed deliberately at every
   * hop rather than defaulted into existence. Same reasoning as
   * `variantOptions` above: on OKS-933 an optional prop let a parent forget to
   * pass it, the component rendered its empty branch forever, and the ticket's
   * headline affordance was dead in the UI with CI green. Required makes the
   * compiler enumerate page → panel → row-actions → dialog.
   */
  product: ShipmentProduct | null
  /**
   * OKS-984 — the KD-2 sentinel identifying the caller. `"shipments"` posts
   * from the queue; omitted from the per-mission panel.
   *
   * ⚠️ A VALUE, never a URL. It selects the post-success redirect AND marks
   * this as the queue caller for the server-side no-tracking confirmation.
   * Both are resolved from one server-side lookup so they cannot disagree.
   */
  returnTo?: "shipments"
  /**
   * OKS-991 — the box's contents, listed read-only in the parcel dialog so the
   * final confirmation states WHAT is being shipped, not only to whom.
   *
   * ⚠️ Read-only by design (UX OQ-6): the dialog does not let the operator
   * deselect items. Deselecting IS « Expédier seul », done from the card,
   * which posts a one-item list through the same endpoint.
   */
  parcelItems?: { key: string; product: ShipmentProduct | null }[]
  /** OKS-991 — « Expédier seul » overrides the trigger's label and aria. */
  triggerLabel?: string
  triggerAriaLabel?: string
  /**
   * OKS-991 — the trigger's visual weight.
   *
   * ⚠️ « Expédier seul » must be SECONDARY (UX §2/§4). PO's walk found it
   * rendering as a filled primary on every item — three stacked orange
   * buttons above the group CTA at 402px, louder than the action they sit
   * under. That pulls the eye toward shipping items ONE AT A TIME, which is
   * the behaviour this whole ticket exists to reduce: the default action must
   * read as "ship the box".
   *
   * Defaults to `default` so the two existing callers (queue row, mission
   * panel) are byte-identical.
   */
  triggerVariant?: "default" | "ghost" | "outline" | "secondary"
  /**
   * OKS-1021 D4 — extra classes for the `mark` trigger only.
   *
   * ⚠️ Exists for ONE caller: the mission panel passes `max-md:min-h-11`, the
   * 44px mobile floor `ui/button.tsx` deliberately does not give `size="sm"`
   * (OKS-874 deferral). A call-site token, not a primitive edit, so the queue's
   * triggers — not part of that ruling — stay byte-identical.
   */
  triggerClassName?: string
}

export function MarkShippedDialog({
  row,
  target,
  mode = "mark",
  variantOptions,
  product,
  returnTo,
  parcelItems,
  triggerLabel,
  triggerAriaLabel,
  triggerVariant = "default",
  triggerClassName,
}: MarkShippedDialogProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  // OKS-1040 (PO re-walk R1) — a base refusal that arrives after this dialog's
  // parcel left Ready (and unmounted the dialog) is shown by the page instead.
  // `null` outside the Shipments queue (the mission panel), which then skips it.
  const shipRefusal = useShipRefusal()
  useEffect(() => {
    if (!open || !shipRefusal) return
    return shipRefusal.registerOpen()
  }, [open, shipRefusal])
  // OKS-1006 D3 — scopes the refused-submit focus lookup to THIS dialog's form.
  const formBodyRef = useRef<HTMLDivElement>(null)
  const initialValue = mode === "edit" ? (row.tracking_number ?? "") : ""
  const [tracking, setTracking] = useState(initialValue)
  // OKS-495 — optional shipment-display fields; prefill from the row in
  // `edit` mode so a re-fire round-trips the current values.
  const initialUrl = mode === "edit" ? (row.tracking_url ?? "") : ""
  const initialEta = mode === "edit" ? (row.delivery_eta ?? "") : ""
  const [trackingUrl, setTrackingUrl] = useState(initialUrl)
  const [deliveryEta, setDeliveryEta] = useState(initialEta)
  const ariaTitle = row.creator.handle
    ? `@${row.creator.handle}`
    : row.creator.name

  // OKS-984 — the inline "ship without tracking" confirmation strip.
  const [confirmingNoTracking, setConfirmingNoTracking] = useState(false)
  // Only the QUEUE caller needs the confirmation, and only when there is no
  // tracking of any kind. On the panel (`returnTo` undefined) blank tracking
  // submits straight through, exactly as it has since OKS-32.
  const needsNoTrackingConfirm =
    returnTo === "shipments" &&
    mode === "mark" &&
    tracking.trim() === "" &&
    trackingUrl.trim() === ""

  // OKS-984 — the queue caller gets the link-first, one-field layout.
  const isQueue = returnTo === "shipments"
  // OKS-989 — each parcel item's product, looked up by `(source, id)`.
  //
  // ⚠️ A KEYED LOOKUP, never positional. `target.items` drives the posted
  // indices and `parcelItems` carries the display data; walking them as
  // parallel arrays is one reordering away from posting item A's variant under
  // item B's id, which writes a wrong retrospective record that nothing
  // downstream can detect. Row identity in this queue is the PAIR — a collab 42
  // and a redemption 42 both exist — so the bare id would not be unique either.
  const parcelProductByKey = new Map(
    (parcelItems ?? []).map((item) => [item.key, item.product]),
  )
  // OKS-989 F1 — how many selects this dialog will actually RENDER.
  //
  // ⚠️ COUNTED FROM THE SAME `>= 2` PREDICATE THE GATE USES, never from
  // `parcelItems.length`. A two-item parcel whose second item is determined
  // renders ONE select, and labelling that one with its product name would be
  // the same duplication F1 removes — the item count and the control count are
  // different numbers, and only the second one is what the label rule is about.
  //
  // ⚠️ Derived, never passed in. A prop would be a second source of truth for
  // something already decided by the render gate below, free to disagree with
  // what is on screen.
  const renderedSelectCount = (parcelItems ?? []).filter(
    (item) => (item.product?.shippable_variants.length ?? 0) >= 2,
  ).length

  const overLimit = tracking.length > MAX_TRACKING_CHARS
  const showCounter =
    tracking.length >= COUNTER_VISIBILITY_THRESHOLD || overLimit

  // OKS-1021 D5 — the parcel's item keys AS THE OPERATOR OPENED IT.
  //
  // ⚠️ KEYS, never a count. The card this dialog mounts under is keyed by
  // `creator_id-ordinal` (`Brand::ShipmentsController#serialise_parcel`), so a
  // round-trip that regroups the queue keeps this component — and its `open`
  // state — while `target.items` now comes from whichever parcel sits at that
  // position. A length comparison misses a same-count swap; a key comparison
  // does not. Taken on open, cleared on close, so a reopen starts fresh.
  const [openedItemKeys, setOpenedItemKeys] = useState<string[] | null>(null)

  function handleOpenChange(next: boolean) {
    setOpen(next)
    setOpenedItemKeys(
      next && target.kind === "parcel"
        ? target.items.map((item) => `${item.source}-${item.id}`)
        : null,
    )
    if (!next) {
      shipRefusal?.settled()
      // Reset to source-of-truth on close — for `edit` mode that's the
      // current row value; for `mark` mode it's empty.
      setTracking(initialValue)
      setTrackingUrl(initialUrl)
      setDeliveryEta(initialEta)
    }
  }

  // Literal `t(...)` calls per OKS-8 i18n health-check posture — the
  // checker's static-analysis regex needs the key as a string literal
  // inside `t("...")`. We branch by mode using bare-string `t(...)`
  // calls (NOT a dynamic key variable) so every key is discoverable.
  const isParcel = target.kind === "parcel"
  const parcelCount = isParcel ? target.items.length : 0
  const currentItemKeys = isParcel
    ? new Set(target.items.map((item) => `${item.source}-${item.id}`))
    : null
  const departedItemCount =
    openedItemKeys && currentItemKeys
      ? openedItemKeys.filter((key) => !currentItemKeys.has(key)).length
      : 0

  // ⚠️ Literal `t("...")` calls per the OKS-8 i18n health check: the static
  // analyser needs the key as a string literal, so these branch rather than
  // building a dynamic key.
  const title = isParcel
    ? t("collabs.mark_shipped.parcel_title")
    : mode === "edit"
      ? t("collabs.mark_shipped.edit_title")
      : t("collabs.mark_shipped.title")
  const cta = isParcel
    ? t("collabs.mark_shipped.parcel_cta", { count: parcelCount })
    : mode === "edit"
      ? t("collabs.mark_shipped.edit_cta")
      : t("collabs.mark_shipped.cta")
  const confirm =
    mode === "edit"
      ? t("collabs.mark_shipped.edit_confirm")
      : t("collabs.mark_shipped.confirm")
  const ariaLabel =
    triggerAriaLabel ??
    (isParcel
      ? t("collabs.mark_shipped.parcel_aria", { title: ariaTitle })
      : mode === "edit"
        ? t("collabs.mark_shipped.edit_aria", { title: ariaTitle })
        : t("collabs.mark_shipped.aria", { title: ariaTitle }))

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger
        render={
          mode === "edit" ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={ariaLabel}
              data-testid="brand-mission-collab-edit-tracking-trigger"
              className="size-7"
            >
              <Pencil aria-hidden="true" className="size-3.5" />
            </Button>
          ) : (
            <Button
              type="button"
              variant={triggerVariant}
              size="sm"
              aria-label={ariaLabel}
              className={triggerClassName}
              data-testid="brand-mission-collab-mark-shipped-trigger"
            >
              <Package aria-hidden="true" className="mr-1 size-4" />
              {triggerLabel ?? cta}
            </Button>
          )
        }
      />
      <DialogContent
        data-testid="brand-mission-collab-mark-shipped-dialog"
        data-mode={mode}
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {/* OKS-1021 D3 — `edit` corrects a recorded shipment; it must not
                tell the operator to « confirmez simplement l'expédition ». */}
            {isParcel
              ? t("collabs.mark_shipped.parcel_body", { count: parcelCount })
              : mode === "edit"
                ? t("collabs.mark_shipped.edit_body")
                : t("collabs.mark_shipped.body")}
          </DialogDescription>
          {/* OKS-1021 D5 — items that were in the parcel when this dialog
              opened and are not any more. Neutral `status`, never destructive
              and never a toast; it sits beside any refusal rather than
              replacing it, and never blocks submit (the server re-derives the
              parcel from the ids it is handed). Derived at render, not stored. */}
          {departedItemCount > 0 && (
            <p
              role="status"
              className="text-muted-foreground text-sm"
              data-testid="shipments-parcel-changed-notice"
            >
              {t("shipments.parcel.changed_notice", {
                count: departedItemCount,
              })}
            </p>
          )}
          {/* OKS-987 — WHAT is being shipped, below the description rather than
              inside the title.

              ⚠️ `DialogTitle` is the dialog's ACCESSIBLE NAME. Folding a
              product and a variant into it would make the announced name a
              paragraph, and would rewrite strings three suites assert on.

              ⚠️ `edit` mode renders the block COMPACT — no variant statement.
              The recorded variant already renders below as static text
              (`variant.recorded`); stating a prediction beside the record
              invites the two to disagree, and only one of them is a fact. */}
          {product && (
            <ShipmentProductBlock product={product} compact={mode === "edit"} />
          )}
          {/* OKS-989 — the box's contents moved INSIDE the `<Form>` below.
              They are no longer purely read-only: in `mark` mode each item
              may carry its own variant control, and a control outside the form
              posts nothing. The list keeps its testid and its order. */}
        </DialogHeader>
        <Form
          action={
            target.kind === "collab"
              ? transitionsMissionCollabPath(target.missionId, row.id)
              : target.kind === "parcel"
                ? shipBrandShipmentsParcelPath()
                : markShippedRedemptionPath(row.id)
          }
          method={formMethodFor(target)}
          options={{ preserveScroll: true }}
          onStart={() => shipRefusal?.settled()}
          onError={(errs) => {
            const raw = (errs as Record<string, string | string[]>).base
            const base = Array.isArray(raw) ? raw[0] : raw
            if (base) shipRefusal?.refused(base)
          }}
          onSuccess={() => handleOpenChange(false)}
        >
          {({ processing, errors }) => {
            // Inertia surfaces `errors[field]` as `string | string[] |
            // undefined`. Rails sends a single string in v1, but type-
            // check honestly so a future multi-error shape doesn't crash.
            const rawError = errors?.tracking_number
            const fieldError = Array.isArray(rawError) ? rawError[0] : rawError
            // OKS-495 — shipment-display field errors (server-validated).
            const rawUrlError = errors?.tracking_url
            const urlError = Array.isArray(rawUrlError)
              ? rawUrlError[0]
              : rawUrlError
            const rawEtaError = errors?.delivery_eta
            const etaError = Array.isArray(rawEtaError)
              ? rawEtaError[0]
              : rawEtaError
            // OKS-996 — a refused shipped-variant correction
            // (`:variant_unavailable`). Same normalisation as its three
            // siblings, for the same reason: Rails sends one string today, and
            // the array shape must not crash a future multi-error response.
            const rawVariantError = errors?.variant_id
            const variantError = Array.isArray(rawVariantError)
              ? rawVariantError[0]
              : rawVariantError

            // OKS-1006 — a refusal with NO field on this dialog (`errors.base`):
            // every parcel refusal that D8 cannot attach to a field, a queue
            // redemption row's `already_actioned`, and KD-12's
            // `confirm_required`. Before OKS-1006 nothing rendered it: the bag
            // kept the dialog open and the operator saw nothing at all.
            const rawBaseError = errors?.base
            const baseError = Array.isArray(rawBaseError)
              ? rawBaseError[0]
              : rawBaseError
            // OKS-1006 D3 — on the queue the number and the date sit inside «
            // Plus de détails ». A refusal on either must not render inside a
            // CLOSED disclosure: with no toast any more, that would be a
            // refusal the operator cannot see.
            const detailsHoldError = Boolean(fieldError || etaError)

            return (
              <div ref={formBodyRef} className="flex flex-col gap-3">
                {/* OKS-1006 D3 — focus follows the refusal AFTER this render
                    commits (never from `onError`, which fires before it —
                    LQE finding 88). */}
                <FocusOnRefusal errors={errors} containerRef={formBodyRef} />
                {/* OKS-985 — `event` dispatches the COLLAB transitions
                    endpoint's per-event policy predicate. The redemption
                    endpoint is single-purpose and does not read it, so it is
                    omitted there rather than sent and ignored: a param the
                    server never permits is one a reader has to check. */}
                {target.kind === "collab" && (
                  <input type="hidden" name="event" value="mark_shipped" />
                )}
                {/* OKS-991/989 — the parcel's items: the hidden identity
                    pair, the product block, and (mark mode) this item's own
                    variant control, all from ONE iteration.

                    ⚠️ `target.items` is the single index source. An earlier
                    shape had the visual list and the hidden inputs walking two
                    parallel arrays, which is one reordering away from posting
                    item A's variant under item B's id — a wrong retrospective
                    record that nothing downstream could detect. The product is
                    looked up by `(source, id)` key, never by position.

                    ⚠️ Indexed names so each item's fields stay associated; the
                    server re-loads every id through its own brand-floored scope
                    and refuses unless they all resolve to ONE parcel. */}
                {target.kind === "parcel" && (
                  <ul
                    className="flex flex-col gap-3"
                    data-testid="mark-shipped-parcel-contents"
                  >
                    {target.items.map((item, index) => {
                      const itemKey = `${item.source}-${item.id}`
                      const itemProduct =
                        parcelProductByKey.get(itemKey) ?? null
                      const itemOptions = itemProduct?.shippable_variants ?? []
                      const selectId = `mark-shipped-parcel-variant-${itemKey}`
                      // OKS-1021 D2 — THIS item's stale-variant refusal.
                      //
                      // ⚠️ A TWO-SIDED STRING CONTRACT `tsc` cannot see: the
                      // server builds `items.collab-<collab id>.variant_id`
                      // (`Brand::ParcelShipmentsController::PARCEL_FIELD_ERRORS`)
                      // and this reads the same shape off `itemKey`. Keyed on
                      // IDENTITY, never `index`: an index key would put the
                      // message under the wrong item the moment the two sides
                      // order the box differently.
                      const rawItemVariantError =
                        errors?.[`items.${itemKey}.variant_id`]
                      const itemVariantError = Array.isArray(
                        rawItemVariantError,
                      )
                        ? rawItemVariantError[0]
                        : rawItemVariantError
                      const itemErrorId = `mark-shipped-parcel-variant-error-${itemKey}`

                      return (
                        <li key={itemKey} className="flex flex-col gap-1.5">
                          <input
                            type="hidden"
                            name={`items[${index}][source]`}
                            value={item.source}
                          />
                          <input
                            type="hidden"
                            name={`items[${index}][id]`}
                            value={item.id}
                          />
                          {itemProduct && (
                            /* ⚠️ `compact` ONLY in edit mode. `compact` means
                               "suppress the variant statement" and nothing
                               else, so lifting it here is what makes the box's
                               contents state which variant each item is —
                               previously the parcel dialog named no variant at
                               all. */
                            <ShipmentProductBlock
                              product={itemProduct}
                              compact={mode === "edit"}
                            />
                          )}
                          {/* Same size rule as the single-item dialog: a
                              control only where there is a real choice. A
                              determined item is already stated by the product
                              block above, and submits nothing. */}
                          {mode === "mark" && itemOptions.length >= 2 && (
                            <>
                              {/* OKS-989 F1 (§3.3) — the label is keyed on the
                                  number of SELECTS, exactly like the render
                                  gate one level down.

                                  ⚠️ The product name exists to tell N controls
                                  apart. At one control there is nothing to tell
                                  it apart from, and the dialog states the
                                  product immediately above it — so the name is
                                  repetition, not disclosure: it renders twice
                                  within three lines and wraps to two lines at
                                  402px doing it. The N-distinct-names reason is
                                  the ONLY reason for the long label, so it
                                  applies exactly where N > 1.

                                  ⚠️ Both keys stay live. `label_for_product` is
                                  not retired — which one applies is a COUNT,
                                  not a surface. */}
                              <Label htmlFor={selectId}>
                                {renderedSelectCount >= 2
                                  ? t(
                                      "collabs.ship.variant.label_for_product",
                                      {
                                        product: itemProduct?.name ?? "",
                                      },
                                    )
                                  : t("collabs.ship.variant.label")}
                              </Label>
                              <select
                                id={selectId}
                                name={`items[${index}][variant_id]`}
                                defaultValue=""
                                className="border-input bg-background h-9 rounded-md border px-3 text-sm max-md:min-h-11"
                                aria-describedby={describedBy(
                                  itemVariantError && itemErrorId,
                                )}
                                aria-invalid={
                                  itemVariantError ? "true" : undefined
                                }
                                data-testid={`mark-shipped-parcel-variant-select-${itemKey}`}
                              >
                                <option value="">
                                  {t("collabs.ship.variant.unspecified")}
                                </option>
                                {itemOptions.map((variant) => (
                                  <option key={variant.id} value={variant.id}>
                                    {variantLabel(variant, t)}
                                  </option>
                                ))}
                              </select>
                            </>
                          )}
                          {/* OKS-1021 D2 — OUTSIDE the `>= 2` gate above (LQE
                              H2): the refusal's redirect re-renders this item
                              with its RE-PINNED set, and a re-pin to one
                              option unmounts the select. A region inside the
                              gate would vanish with it. The focus marker makes
                              the message itself the focus target when no
                              invalid control is left to take it. */}
                          {itemVariantError && (
                            <p
                              id={itemErrorId}
                              role="alert"
                              aria-live="polite"
                              tabIndex={-1}
                              {...{ [REFUSAL_FOCUS_ATTR]: "" }}
                              className="text-destructive text-xs"
                              data-testid={itemErrorId}
                            >
                              {itemVariantError}
                            </p>
                          )}
                        </li>
                      )
                    })}
                  </ul>
                )}
                {/* OKS-984 KD-2 — the caller sentinel. A VALUE, never a URL. */}
                {returnTo && (
                  <input type="hidden" name="return_to" value={returnTo} />
                )}

                {/* OKS-934 — the variant shipped.

                    ⚠️ `name="variant_id"` is FLAT, copying its three siblings
                    on this very form (`tracking_number`, `tracking_url`,
                    `delivery_eta`), which the controller reads through a flat
                    `params.permit(...)`. NOT `collab[variant_id]`.

                    That convention is stated here because the tell is
                    available in advance: four fields on one form, three of
                    them flat and established. On OKS-933 the new field in a
                    file went NESTED while its siblings were flat, the
                    controller read top-level, and EVERY attach landed unpinned
                    with CI green — nothing on this branch compares the two
                    sides.

                    ⚠️ OKS-996 — `edit` mode has its OWN control below, on the
                    same `>= 2` size rule, and the two branches stay separate
                    because their first option differs in MEANING: `mark`'s
                    « Non précisé » means "do not record", `edit`'s « Laisser
                    tel quel » means "do not change". One shared option list
                    with one shared string is how a record gets nulled by an
                    operator who read the other mode's meaning. */}
                {/* OKS-989 — DETERMINED: exactly one variant can ship, so
                    there is nothing to decide. A sentence, never a control.

                    ⚠️ NOT a one-option select and NOT a disabled one. A select
                    with a single choice teaches the operator the value is
                    theirs to change; a greyed-out control reads as "broken" or
                    "you lack permission". Both misdescribe a fact that is
                    simply settled — the same rule OKS-984 applied when it
                    DELETED the redemption edit affordance rather than
                    disabling it.

                    ⚠️ NOTHING IS SUBMITTED HERE — no hidden `variant_id`. The
                    server records the sole member itself
                    (`Collabs::Transition#sole_shippable_variant`). Echoing the
                    id back would route it through the STRICT lookup, so a
                    brand re-pinning between render and submit would get an
                    error page instead of OKS-990 R8b's deliberate degradation
                    to an empty label. Do not "close the loop" here. */}
                {mode === "mark" && variantOptions.length === 1 && (
                  <p
                    className="text-muted-foreground text-xs"
                    data-testid="brand-mission-collab-mark-shipped-variant-determined"
                  >
                    {t("collabs.ship.variant.statement.fixed", {
                      label: variantLabel(variantOptions[0], t),
                    })}
                  </p>
                )}

                {/* ⚠️ `>= 2`, NOT `> 0` (OKS-989 KD-2). The control appears
                    only where there is a real choice; a one-member set gets
                    the statement above.

                    This gate MOVES TOGETHER WITH
                    `Collabs::Transition#sole_shippable_variant`. Widen it back
                    to `> 0` on its own and a single-variant mission renders a
                    select whose value the write path now overrides — a control
                    that does nothing, which is the shape the `edit`-mode note
                    below exists to prevent. Narrow the server alone and a
                    previously-recorded ship silently becomes null. */}
                {mode === "mark" && variantOptions.length >= 2 && (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="mark-shipped-variant">
                      {t("collabs.ship.variant.label")}
                    </Label>
                    <select
                      id="mark-shipped-variant"
                      name="variant_id"
                      defaultValue=""
                      className="border-input bg-background h-9 rounded-md border px-3 text-sm max-md:min-h-11"
                      aria-describedby={describedBy(
                        variantError && "mark-shipped-variant-error",
                      )}
                      aria-invalid={variantError ? "true" : undefined}
                      data-testid="brand-mission-collab-mark-shipped-variant-select"
                    >
                      {/* Optional by design (PRD OQ-4): a brand who does not
                          know must not be forced to invent. A false record is
                          worse than none. */}
                      <option value="">
                        {t("collabs.ship.variant.unspecified")}
                      </option>
                      {/* OKS-987 — composed by the SHARED helper, which the
                          queue row also calls. Inlining it here again is how
                          the row and this select come to name one variant two
                          ways, which is the ambiguity this ticket removes.
                          The helper also supplies the empty-title fallback
                          (`title` is NOT NULL DEFAULT ''). */}
                      {variantOptions.map((variant) => (
                        <option key={variant.id} value={variant.id}>
                          {variantLabel(variant, t)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {/* OKS-1021 D1 — the ship-time refusal (`:variant_unavailable`):
                    the submitted variant left the shippable set while this
                    dialog sat open (a colleague re-pinned it).

                    ⚠️ OUTSIDE both gates above, never inside the `>= 2` one
                    (LQE H2). The refusal's redirect re-renders this dialog with
                    the RE-PINNED set; a re-pin to one option swaps the select
                    for the determined sentence, and a region inside the gate
                    would unmount with it — a refusal that paints nothing.

                    ⚠️ `!isParcel`: a parcel's stale item has its OWN region under
                    its own select (keyed per item), and the parcel endpoint
                    never sends a bare `variant_id`.

                    The focus marker + `tabIndex={-1}` make the message the focus
                    target when no invalid select remains to take it
                    (`focusFirstRefusal` prefers `[aria-invalid]`). */}
                {mode === "mark" && !isParcel && variantError && (
                  <p
                    id="mark-shipped-variant-error"
                    role="alert"
                    aria-live="polite"
                    tabIndex={-1}
                    {...{ [REFUSAL_FOCUS_ATTR]: "" }}
                    className="text-destructive text-xs"
                    data-testid="brand-mission-collab-mark-shipped-variant-error"
                  >
                    {variantError}
                  </p>
                )}

                {mode === "edit" && row.shipped_variant_label ? (
                  <p
                    className="text-muted-foreground text-xs"
                    data-testid="brand-mission-collab-shipped-variant-readonly"
                  >
                    {t("collabs.ship.variant.recorded", {
                      label: row.shipped_variant_label,
                    })}
                  </p>
                ) : null}

                {/* OKS-996 (BRIEF D1/D3, AC1/AC1b) — CORRECTING the recorded
                    variant. The record renders above as a sentence; this is the
                    only affordance that can change it.

                    ⚠️ ON THE PRIMARY SURFACE, above the tracking field, and
                    NEVER behind a disclosure (AC1b). Not a style choice: moving
                    it inside « Plus de détails » keeps every render and write
                    arm green — it still renders and still posts — so only a
                    placement assertion can see the regression, and the
                    capability this whole ticket exists to add would be hidden
                    about as well as its absence hid it. OKS-989 §3.1 ruled this
                    for this control on this component.

                    ⚠️ AND NEVER A `<details>`. The `more_details` disclosure is
                    ~60 lines below and is the natural thing to copy; `<details>`
                    HIDES WITHOUT UNMOUNTING, so its `<select>` would still be in
                    the FormData on every tracking-only edit and would post
                    whatever it held. Conditional render or nothing.

                    ⚠️ NOTHING IS PRE-SELECTED, and `keep` IS THE FIRST OPTION.
                    Those are two requirements, and the order matters: the safety
                    comes from the first option being the blank one, because that
                    is what the browser falls back to when no `selected` matches.
                    « the operator opened this dialog to fix a tracking typo and
                    submitted » is then a no-op BY CONSTRUCTION rather than by
                    their care — `keep` posts `""` and the server leaves the pair
                    alone on a blank (D3).

                    ⚠️ AN EARLIER VERSION OF THIS NOTE OVERSTATED THE HAZARD AND
                    IS CORRECTED HERE. It claimed a pre-selected control would
                    "open on a value it cannot show and one submit would overwrite
                    the record" for the three states no current option can
                    represent (recorded variant un-pinned; variant hard-deleted
                    with the label surviving; nothing recorded). LQE measured that
                    false: with `keep` first, the fallback IS `keep`, so
                    pre-selecting the RECORDED variant is merely useless — on a row
                    that already holds it the write lands byte-identical, and on a
                    row whose record is unrepresentable it degrades to the blank.
                    The spelling that actually hurts is pre-selecting a REAL
                    option (or reordering `keep` out of first place), which would
                    write a variant nobody chose on every tracking-only edit.
                    Keep the claim matched to the mechanism: it is the blank-first
                    ORDER that is load-bearing, not the absence of a `selected`
                    attribute on its own.

                    ⚠️ `collabs.ship.variant.keep`, NOT `unspecified`. Same blank
                    value, deliberately different sentence: here it means "do not
                    change", there it means "do not record". One string across
                    both meanings is how a possession fact gets nulled by someone
                    who read the other one.

                    ⚠️ Size rule: `correctionOffered` (OKS-1028 D1). At ≥ 2 the
                    same rule as `mark` mode (OKS-996 D1). At exactly 1 the
                    control shows unless the row records THAT variant, compared
                    by id: nothing recorded, an un-pinned record and a
                    hard-deleted one are all genuine corrections. When the row
                    does record the one offered variant (and at 0) there is no
                    control and no new sentence — the `recorded` line above
                    already states the fact, and a one-option select would
                    teach the operator that a settled value is theirs to change.

                    ⚠️ The id is on the row now, which makes
                    `defaultValue={row.shipped_product_variant_id}` look like a
                    tidy edit. Keep `defaultValue=""`. On a record OUTSIDE the
                    options it degrades to « keep » (the fallback above) and is
                    merely useless — so no arm with such a fixture can see it.
                    On a record INSIDE the options it opens on that variant, and
                    a tracking-only edit posts `variant_id`:
                    `Collabs::Transition#recorded_variant_matches?` compares the
                    label too, so after a rename that "no-op" re-stamps the
                    label and writes a correction audit row nobody asked for.
                    Killed by the record-inside arm in
                    `correct-variant-error-oks996.test.tsx` and E4's shipped_edit
                    leg; A13(d) (record outside) cannot kill it. */}
                {mode === "edit" &&
                  correctionOffered(
                    variantOptions,
                    row.shipped_product_variant_id,
                  ) && (
                    <div className="flex flex-col gap-1.5">
                      <Label
                        htmlFor="correct-shipped-variant"
                        data-testid="brand-mission-collab-correct-variant-label"
                      >
                        {t("collabs.ship.variant.correct_label")}
                      </Label>
                      <select
                        id="correct-shipped-variant"
                        name="variant_id"
                        defaultValue=""
                        className="border-input bg-background h-9 rounded-md border px-3 text-sm max-md:min-h-11"
                        aria-describedby={describedBy(
                          "correct-shipped-variant-helper",
                          variantError && "correct-shipped-variant-error",
                        )}
                        aria-invalid={variantError ? "true" : undefined}
                        data-testid="brand-mission-collab-correct-variant-select"
                      >
                        <option value="">
                          {t("collabs.ship.variant.keep")}
                        </option>
                        {variantOptions.map((variant) => (
                          <option key={variant.id} value={variant.id}>
                            {variantLabel(variant, t)}
                          </option>
                        ))}
                      </select>
                      <span
                        id="correct-shipped-variant-helper"
                        className="text-muted-foreground text-xs"
                      >
                        {t("collabs.ship.variant.correct_helper")}
                      </span>
                    </div>
                  )}
                {/* OKS-996 (AC9) — the refused correction. The server sends
                    `errors.variant_id` (`:variant_unavailable`) when the
                    submitted variant left the shippable scope while this
                    dialog sat open.

                    ⚠️ THIS REGION IS THE OTHER HALF OF THE REFUSAL. Routing
                    the error server-side is not rendering it: before OKS-996
                    this component read only `errors.tracking_number` /
                    `tracking_url` / `delivery_eta`, so the mapping alone
                    would have produced a dialog that stays open and says
                    NOTHING while a request spec asserting "the error is
                    returned" stayed green. A FIELD error, never `base`: the
                    operator needs the message beside the control they must
                    change (BRIEF-OKS-996 §5 Error row; precedent OKS-923),
                    and `base` is this dialog's dialog-level message for
                    refusals with no field (OKS-1006).

                    OKS-1021 — hoisted OUT of the `correctionOffered` gate: a
                    re-pin to one option after a refusal can remove the control
                    (the row records the one variant left), and the refusal
                    must still paint. Same id,
                    testid and string as before, so this is a strict superset
                    of the OKS-996 rendering. */}
                {mode === "edit" && variantError && (
                  <p
                    id="correct-shipped-variant-error"
                    role="alert"
                    aria-live="polite"
                    tabIndex={-1}
                    {...{ [REFUSAL_FOCUS_ATTR]: "" }}
                    className="text-destructive text-xs"
                    data-testid="brand-mission-collab-correct-variant-error"
                  >
                    {variantError}
                  </p>
                )}

                {/* ⚠️ Branch on PRESENCE, never on truthiness. The key is
                    ABSENT outside the shipment window (PRD §4), so `in` is the
                    test; a truthiness check would also hide the line for a
                    creator who is inside the window but has set no sizes, and
                    that case has its own copy. */}
                {mode === "mark" && "creator_sizes" in row && (
                  <p
                    className="text-muted-foreground text-xs"
                    data-testid="brand-mission-collab-creator-sizes"
                  >
                    {(row.creator_sizes?.apparel ?? row.creator_sizes?.shoe)
                      ? t("collabs.ship.sizes.line", {
                          apparel: row.creator_sizes?.apparel ?? "—",
                          shoe: row.creator_sizes?.shoe ?? "—",
                        })
                      : t("collabs.ship.sizes.none")}
                  </p>
                )}
                {/*
                  OKS-984 — QUEUE FIELD ORDER (UX Spec §2.2 / CTPO decision 2).

                  ⚠️ The queue asks for ONE field: the tracking LINK. The
                  number and the delivery date collapse behind "More details",
                  so the default path is one field and one button.

                  ⚠️ THE VARIANT IS NOT IN THE DISCLOSURE and never has been —
                  its block renders above this branch, on the primary surface,
                  for both callers. OKS-989 ruled that deliberately rather than
                  by inheritance: the row states the variant question one click
                  earlier, so the dialog is where it gets ANSWERED, and burying
                  the answer is what produced « Variante expédiée non
                  renseignée » on every queue ship. Decision 2's intent
                  survives because the control appears only where there is a
                  real choice (>= 2 options), so an unambiguous parcel is still
                  one field and one button.

                  The number is the thing an operator usually does NOT have —
                  KD-8 derives it from the link for recognised carriers — so
                  offering it first asks for the harder value and buries the
                  one they were told to paste.

                  ⚠️ SCOPED to the queue caller. The per-mission panel keeps
                  its existing order and its existing specs; UX §2.2 changes
                  the order "for the queue", not everywhere.
                */}
                {isQueue ? (
                  <>
                    {/* OKS-495 — Tracking link (https). Optional. */}
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="mark-shipped-tracking-url">
                        {t("collabs.tracking_url.label")}
                      </Label>
                      <Input
                        id="mark-shipped-tracking-url"
                        name="tracking_url"
                        type="url"
                        inputMode="url"
                        value={trackingUrl}
                        onChange={(e) => setTrackingUrl(e.target.value)}
                        aria-describedby={describedBy(
                          "mark-shipped-tracking-url-helper",
                          urlError && "mark-shipped-tracking-url-error",
                        )}
                        aria-invalid={urlError ? "true" : undefined}
                        placeholder={t("collabs.tracking_url.placeholder")}
                        data-testid="brand-mission-collab-mark-shipped-tracking-url-input"
                      />
                      <span
                        id="mark-shipped-tracking-url-helper"
                        className="text-muted-foreground text-xs"
                      >
                        {t("collabs.tracking_url.helper")}
                      </span>
                      {urlError && (
                        <p
                          id="mark-shipped-tracking-url-error"
                          role="alert"
                          aria-live="polite"
                          className="text-destructive text-xs"
                          data-testid="brand-mission-collab-mark-shipped-tracking-url-error"
                        >
                          {urlError}
                        </p>
                      )}
                    </div>

                    <details
                      data-testid="shipments-mark-shipped-more-details"
                      open={detailsHoldError || undefined}
                    >
                      <summary className="text-muted-foreground cursor-pointer text-xs">
                        {t("collabs.mark_shipped.more_details")}
                      </summary>
                      <div className="mt-3 flex flex-col gap-4">
                        <div className="flex flex-col gap-1.5">
                          <Label htmlFor="mark-shipped-tracking">
                            {t("collabs.tracking_number.label")}
                          </Label>
                          <Input
                            id="mark-shipped-tracking"
                            name="tracking_number"
                            type="text"
                            value={tracking}
                            onChange={(e) => setTracking(e.target.value)}
                            maxLength={MAX_TRACKING_CHARS + 5}
                            aria-describedby={describedBy(
                              "mark-shipped-tracking-helper",
                              "mark-shipped-tracking-counter",
                              fieldError && "mark-shipped-tracking-error",
                            )}
                            aria-invalid={fieldError ? "true" : undefined}
                            placeholder={t(
                              "collabs.tracking_number.placeholder",
                            )}
                            className="font-mono"
                            data-testid="brand-mission-collab-mark-shipped-tracking-input"
                          />
                          <div className="flex items-center justify-between text-xs">
                            <span
                              id="mark-shipped-tracking-helper"
                              className="text-muted-foreground"
                            >
                              {t("collabs.tracking_number.helper")}
                            </span>
                            <span
                              id="mark-shipped-tracking-counter"
                              className={cn(
                                "tabular-nums",
                                overLimit
                                  ? "text-destructive font-medium"
                                  : showCounter
                                    ? "text-muted-foreground"
                                    : "sr-only",
                              )}
                              data-testid="brand-mission-collab-mark-shipped-counter"
                            >
                              {t("collabs.tracking_number.counter", {
                                count: tracking.length,
                              })}
                            </span>
                          </div>
                          {fieldError && (
                            <p
                              id="mark-shipped-tracking-error"
                              role="alert"
                              aria-live="polite"
                              className="text-destructive text-xs"
                              data-testid="brand-mission-collab-mark-shipped-error"
                            >
                              {fieldError}
                            </p>
                          )}
                        </div>

                        {/* OKS-495 — Delivery date (ETA). Optional. */}
                        <div className="flex flex-col gap-1.5">
                          <Label htmlFor="mark-shipped-delivery-eta">
                            {t("collabs.delivery_eta.label")}
                          </Label>
                          <Input
                            id="mark-shipped-delivery-eta"
                            name="delivery_eta"
                            type="date"
                            value={deliveryEta}
                            onChange={(e) => setDeliveryEta(e.target.value)}
                            aria-describedby={describedBy(
                              "mark-shipped-delivery-eta-helper",
                              etaError && "mark-shipped-delivery-eta-error",
                            )}
                            aria-invalid={etaError ? "true" : undefined}
                            data-testid="brand-mission-collab-mark-shipped-delivery-eta-input"
                          />
                          <span
                            id="mark-shipped-delivery-eta-helper"
                            className="text-muted-foreground text-xs"
                          >
                            {t("collabs.delivery_eta.helper")}
                          </span>
                          {etaError && (
                            <p
                              id="mark-shipped-delivery-eta-error"
                              role="alert"
                              aria-live="polite"
                              className="text-destructive text-xs"
                              data-testid="brand-mission-collab-mark-shipped-delivery-eta-error"
                            >
                              {etaError}
                            </p>
                          )}
                        </div>
                      </div>
                    </details>
                  </>
                ) : (
                  <>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="mark-shipped-tracking">
                        {t("collabs.tracking_number.label")}
                      </Label>
                      <Input
                        id="mark-shipped-tracking"
                        name="tracking_number"
                        type="text"
                        value={tracking}
                        onChange={(e) => setTracking(e.target.value)}
                        maxLength={MAX_TRACKING_CHARS + 5}
                        aria-describedby={describedBy(
                          "mark-shipped-tracking-helper",
                          "mark-shipped-tracking-counter",
                          fieldError && "mark-shipped-tracking-error",
                        )}
                        aria-invalid={fieldError ? "true" : undefined}
                        placeholder={t("collabs.tracking_number.placeholder")}
                        className="font-mono"
                        data-testid="brand-mission-collab-mark-shipped-tracking-input"
                      />
                      <div className="flex items-center justify-between text-xs">
                        <span
                          id="mark-shipped-tracking-helper"
                          className="text-muted-foreground"
                        >
                          {t("collabs.tracking_number.helper")}
                        </span>
                        <span
                          id="mark-shipped-tracking-counter"
                          className={cn(
                            "tabular-nums",
                            overLimit
                              ? "text-destructive font-medium"
                              : showCounter
                                ? "text-muted-foreground"
                                : "sr-only",
                          )}
                          data-testid="brand-mission-collab-mark-shipped-counter"
                        >
                          {t("collabs.tracking_number.counter", {
                            count: tracking.length,
                          })}
                        </span>
                      </div>
                      {fieldError && (
                        <p
                          id="mark-shipped-tracking-error"
                          role="alert"
                          aria-live="polite"
                          className="text-destructive text-xs"
                          data-testid="brand-mission-collab-mark-shipped-error"
                        >
                          {fieldError}
                        </p>
                      )}
                    </div>

                    {/* OKS-495 — Tracking link (https). Optional. */}
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="mark-shipped-tracking-url">
                        {t("collabs.tracking_url.label")}
                      </Label>
                      <Input
                        id="mark-shipped-tracking-url"
                        name="tracking_url"
                        type="url"
                        inputMode="url"
                        value={trackingUrl}
                        onChange={(e) => setTrackingUrl(e.target.value)}
                        aria-describedby={describedBy(
                          "mark-shipped-tracking-url-helper",
                          urlError && "mark-shipped-tracking-url-error",
                        )}
                        aria-invalid={urlError ? "true" : undefined}
                        placeholder={t("collabs.tracking_url.placeholder")}
                        data-testid="brand-mission-collab-mark-shipped-tracking-url-input"
                      />
                      <span
                        id="mark-shipped-tracking-url-helper"
                        className="text-muted-foreground text-xs"
                      >
                        {t("collabs.tracking_url.helper")}
                      </span>
                      {urlError && (
                        <p
                          id="mark-shipped-tracking-url-error"
                          role="alert"
                          aria-live="polite"
                          className="text-destructive text-xs"
                          data-testid="brand-mission-collab-mark-shipped-tracking-url-error"
                        >
                          {urlError}
                        </p>
                      )}
                    </div>

                    {/* OKS-495 — Delivery date (ETA). Optional. */}
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="mark-shipped-delivery-eta">
                        {t("collabs.delivery_eta.label")}
                      </Label>
                      <Input
                        id="mark-shipped-delivery-eta"
                        name="delivery_eta"
                        type="date"
                        value={deliveryEta}
                        onChange={(e) => setDeliveryEta(e.target.value)}
                        aria-describedby={describedBy(
                          "mark-shipped-delivery-eta-helper",
                          etaError && "mark-shipped-delivery-eta-error",
                        )}
                        aria-invalid={etaError ? "true" : undefined}
                        data-testid="brand-mission-collab-mark-shipped-delivery-eta-input"
                      />
                      <span
                        id="mark-shipped-delivery-eta-helper"
                        className="text-muted-foreground text-xs"
                      >
                        {t("collabs.delivery_eta.helper")}
                      </span>
                      {etaError && (
                        <p
                          id="mark-shipped-delivery-eta-error"
                          role="alert"
                          aria-live="polite"
                          className="text-destructive text-xs"
                          data-testid="brand-mission-collab-mark-shipped-delivery-eta-error"
                        >
                          {etaError}
                        </p>
                      )}
                    </div>
                  </>
                )}
                {/* OKS-1006 — the dialog-level message for a refusal with NO
                    field here (`errors.base`). Rendered ONLY when present — an
                    always-mounted empty `role="alert"` would be announced as
                    nothing and would read as a second channel in every "no
                    message" assertion. A response carries either field keys or
                    `base`, never both (MissionCollabsController /
                    Brand::ParcelShipmentsController build one or the other), so
                    this never doubles up with an inline field error.

                    Same shape the rewards-page dialog already uses for these
                    redemption refusals; the text is the server's string,
                    verbatim. `tabIndex={-1}` + the focus marker: when this is
                    the only error, focus lands here (D3). */}
                {baseError && (
                  <Alert
                    variant="destructive"
                    tabIndex={-1}
                    {...{ [REFUSAL_FOCUS_ATTR]: "" }}
                    data-testid="shipments-mark-shipped-base-error"
                  >
                    <AlertDescription>{baseError}</AlertDescription>
                  </Alert>
                )}
                {/*
                  OKS-984 UX-OQ-3 — ONE dialog, TWO footers.

                  Shipping with no tracking of any kind from the queue needs an
                  explicit confirmation. It swaps THIS footer in place rather
                  than opening a nested dialog: nesting breaks focus management
                  and reads as an error, when this is an ordinary choice.

                  ⚠️ The strip is the AFFORDANCE. The guard is server-side
                  (`no_tracking_confirmation_missing?`), scoped to the queue
                  caller so the panel's long-standing "blank means ship without
                  tracking" contract is untouched. Asserting the refusal
                  through this component would measure its willingness to
                  submit, which comes apart from the server's willingness to
                  accept exactly when the server guard is what is missing.
                */}
                {confirmingNoTracking ? (
                  <div
                    className="flex flex-col gap-3"
                    data-testid="shipments-mark-shipped-no-tracking-strip"
                  >
                    <p className="text-muted-foreground text-sm" role="alert">
                      {t("collabs.mark_shipped.no_tracking_warning")}
                    </p>
                    <input type="hidden" name="confirm_no_tracking" value="1" />
                    <DialogFooter>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setConfirmingNoTracking(false)}
                        data-testid="shipments-mark-shipped-back"
                      >
                        {t("collabs.mark_shipped.back")}
                      </Button>
                      <Button
                        type="submit"
                        variant="default"
                        disabled={processing}
                        autoFocus
                        data-testid="shipments-mark-shipped-confirm"
                      >
                        {t("collabs.mark_shipped.ship_without_tracking")}
                      </Button>
                    </DialogFooter>
                  </div>
                ) : (
                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleOpenChange(false)}
                      data-testid="brand-mission-collab-mark-shipped-cancel"
                    >
                      {t("collabs.mark_shipped.cancel")}
                    </Button>
                    <Button
                      type={needsNoTrackingConfirm ? "button" : "submit"}
                      variant="default"
                      disabled={processing || overLimit}
                      autoFocus
                      onClick={
                        needsNoTrackingConfirm
                          ? () => setConfirmingNoTracking(true)
                          : undefined
                      }
                      data-testid="brand-mission-collab-mark-shipped-confirm"
                    >
                      {confirm}
                    </Button>
                  </DialogFooter>
                )}
              </div>
            )
          }}
        </Form>
      </DialogContent>
    </Dialog>
  )
}

/**
 * OKS-1006 D3 — renders nothing; exists so the after-commit focus hook can
 * read the `<Form>` render-prop `errors` (a render prop is not a component, so
 * it cannot call a hook itself).
 */
function FocusOnRefusal({
  errors,
  containerRef,
}: {
  errors: Record<string, unknown> | undefined
  containerRef: RefObject<HTMLDivElement | null>
}) {
  useFocusOnRefusal(errors, containerRef)
  return null
}

/**
 * The HTTP verb each target expects.
 *
 * ⚠️ Derived from the target rather than hardcoded per branch: the collab
 * transitions endpoint is POST, the redemption member route is PATCH, and the
 * parcel collection route is POST. Getting this wrong is a 404 that reads like
 * a routing bug rather than a verb mismatch.
 */
function formMethodFor(target: MarkShippedTarget): "post" | "patch" {
  return target.kind === "redemption" ? "patch" : "post"
}
